/**
 * User Journey Tracker
 *
 * Captures and persists user attribution and journey data across sessions.
 * Provides first-touch and last-touch attribution for conversion tracking.
 *
 * Data is stored in:
 * - localStorage: First-touch attribution (permanent until cleared)
 * - sessionStorage: Current session data (clears on browser close)
 */

// ============================================
// Types
// ============================================

export interface UTMParams {
	utm_source: string | null;
	utm_medium: string | null;
	utm_campaign: string | null;
	utm_term: string | null;
	utm_content: string | null;
}

export interface TouchAttribution {
	utmParams: UTMParams;
	referrer: string | null;
	landingPage: string;
	timestamp: number;
}

export interface SessionData {
	sessionId: string;
	startTime: number;
	pageViews: PageView[];
	lastActivity: number;
}

export interface PageView {
	path: string;
	title: string;
	timestamp: number;
}

export interface JourneyData {
	// Attribution
	firstTouch: TouchAttribution | null;
	lastTouch: TouchAttribution | null;

	// Current session
	session: SessionData | null;

	// Computed fields
	isReturningUser: boolean;
	daysSinceFirstVisit: number | null;
	totalPageViews: number;

	// For API calls
	attribution: AttributionSummary;
}

export interface AttributionSummary {
	// First touch
	firstSource: string | null;
	firstMedium: string | null;
	firstCampaign: string | null;
	firstReferrer: string | null;
	firstLandingPage: string | null;
	firstVisitDate: string | null;

	// Last touch (if different from first)
	lastSource: string | null;
	lastMedium: string | null;
	lastCampaign: string | null;

	// Session
	sessionId: string | null;
	pageViewsInSession: number;

	// Meta
	isReturningUser: boolean;
	daysSinceFirstVisit: number | null;
}

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
	FIRST_TOUCH: "journey:first_touch",
	LAST_TOUCH: "journey:last_touch",
	SESSION: "journey:session",
	VISITOR_ID: "journey:visitor_id",
} as const;

// Session timeout: 30 minutes of inactivity
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// Max page views to store per session (prevent memory bloat)
const MAX_PAGE_VIEWS = 50;

// ============================================
// Utility Functions
// ============================================

function generateId(): string {
	return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function isClient(): boolean {
	return typeof window !== "undefined";
}

function safeGetItem(storage: Storage, key: string): string | null {
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

function safeSetItem(storage: Storage, key: string, value: string): void {
	try {
		storage.setItem(key, value);
	} catch {
		// Storage full or unavailable - silently fail
	}
}

function safeRemoveItem(storage: Storage, key: string): void {
	try {
		storage.removeItem(key);
	} catch {
		// Silently fail
	}
}

function parseJSON<T>(json: string | null): T | null {
	if (!json) return null;
	try {
		return JSON.parse(json) as T;
	} catch {
		return null;
	}
}

// ============================================
// UTM Parameter Extraction
// ============================================

function extractUTMParams(): UTMParams {
	if (!isClient()) {
		return {
			utm_source: null,
			utm_medium: null,
			utm_campaign: null,
			utm_term: null,
			utm_content: null,
		};
	}

	const params = new URLSearchParams(window.location.search);

	return {
		utm_source: params.get("utm_source"),
		utm_medium: params.get("utm_medium"),
		utm_campaign: params.get("utm_campaign"),
		utm_term: params.get("utm_term"),
		utm_content: params.get("utm_content"),
	};
}

function hasUTMParams(params: UTMParams): boolean {
	return !!(
		params.utm_source ||
		params.utm_medium ||
		params.utm_campaign ||
		params.utm_term ||
		params.utm_content
	);
}

function getReferrer(): string | null {
	if (!isClient()) return null;

	const referrer = document.referrer;
	if (!referrer) return null;

	// Ignore self-referrals
	try {
		const referrerHost = new URL(referrer).hostname;
		const currentHost = window.location.hostname;
		if (referrerHost === currentHost) return null;
	} catch {
		// Invalid URL
	}

	return referrer;
}

// ============================================
// Storage Operations
// ============================================

function getFirstTouch(): TouchAttribution | null {
	if (!isClient()) return null;
	return parseJSON<TouchAttribution>(
		safeGetItem(localStorage, STORAGE_KEYS.FIRST_TOUCH),
	);
}

function setFirstTouch(touch: TouchAttribution): void {
	if (!isClient()) return;
	safeSetItem(localStorage, STORAGE_KEYS.FIRST_TOUCH, JSON.stringify(touch));
}

function getLastTouch(): TouchAttribution | null {
	if (!isClient()) return null;
	return parseJSON<TouchAttribution>(
		safeGetItem(localStorage, STORAGE_KEYS.LAST_TOUCH),
	);
}

function setLastTouch(touch: TouchAttribution): void {
	if (!isClient()) return;
	safeSetItem(localStorage, STORAGE_KEYS.LAST_TOUCH, JSON.stringify(touch));
}

function getSession(): SessionData | null {
	if (!isClient()) return null;
	return parseJSON<SessionData>(
		safeGetItem(sessionStorage, STORAGE_KEYS.SESSION),
	);
}

function setSession(session: SessionData): void {
	if (!isClient()) return;
	safeSetItem(sessionStorage, STORAGE_KEYS.SESSION, JSON.stringify(session));
}

function getVisitorId(): string | null {
	if (!isClient()) return null;
	return safeGetItem(localStorage, STORAGE_KEYS.VISITOR_ID);
}

function setVisitorId(id: string): void {
	if (!isClient()) return;
	safeSetItem(localStorage, STORAGE_KEYS.VISITOR_ID, id);
}

// ============================================
// Core Tracking Functions
// ============================================

/**
 * Initialize journey tracking on page load.
 * Should be called once when the app loads.
 */
export function initJourneyTracker(): void {
	if (!isClient()) return;

	const now = Date.now();
	const utmParams = extractUTMParams();
	const referrer = getReferrer();
	const landingPage = window.location.pathname + window.location.search;

	// Ensure visitor ID exists
	if (!getVisitorId()) {
		setVisitorId(generateId());
	}

	// First touch: Only set if never set before
	const existingFirstTouch = getFirstTouch();
	if (!existingFirstTouch) {
		const firstTouch: TouchAttribution = {
			utmParams,
			referrer,
			landingPage,
			timestamp: now,
		};
		setFirstTouch(firstTouch);

		// Also set as last touch initially
		setLastTouch(firstTouch);
	} else if (hasUTMParams(utmParams) || referrer) {
		// Update last touch if we have new UTM params or external referrer
		const lastTouch: TouchAttribution = {
			utmParams: hasUTMParams(utmParams)
				? utmParams
				: existingFirstTouch.utmParams,
			referrer: referrer || existingFirstTouch.referrer,
			landingPage,
			timestamp: now,
		};
		setLastTouch(lastTouch);
	}

	// Session management
	let session = getSession();
	const isNewSession =
		!session || now - session.lastActivity > SESSION_TIMEOUT_MS;

	if (isNewSession || !session) {
		session = {
			sessionId: generateId(),
			startTime: now,
			pageViews: [],
			lastActivity: now,
		};
	} else {
		session.lastActivity = now;
	}

	setSession(session);

	// Track initial page view
	trackPageView();
}

/**
 * Track a page view in the current session.
 */
export function trackPageView(customTitle?: string): void {
	if (!isClient()) return;

	const session = getSession();
	if (!session) {
		// Session not initialized, init tracker first
		initJourneyTracker();
		return;
	}

	const pageView: PageView = {
		path: window.location.pathname,
		title: customTitle || document.title || window.location.pathname,
		timestamp: Date.now(),
	};

	// Add to page views, keeping only last N
	session.pageViews.push(pageView);
	if (session.pageViews.length > MAX_PAGE_VIEWS) {
		session.pageViews = session.pageViews.slice(-MAX_PAGE_VIEWS);
	}

	session.lastActivity = Date.now();
	setSession(session);
}

/**
 * Get all journey data for conversion tracking.
 */
export function getJourneyData(): JourneyData {
	const firstTouch = getFirstTouch();
	const lastTouch = getLastTouch();
	const session = getSession();

	const now = Date.now();
	const daysSinceFirstVisit = firstTouch
		? Math.floor((now - firstTouch.timestamp) / (1000 * 60 * 60 * 24))
		: null;

	const isReturningUser = firstTouch
		? now - firstTouch.timestamp > SESSION_TIMEOUT_MS
		: false;

	return {
		firstTouch,
		lastTouch,
		session,
		isReturningUser,
		daysSinceFirstVisit,
		totalPageViews: session?.pageViews.length || 0,
		attribution: buildAttributionSummary(
			firstTouch,
			lastTouch,
			session,
			isReturningUser,
			daysSinceFirstVisit,
		),
	};
}

/**
 * Get attribution summary formatted for API calls.
 */
export function getAttributionSummary(): AttributionSummary {
	return getJourneyData().attribution;
}

function buildAttributionSummary(
	firstTouch: TouchAttribution | null,
	lastTouch: TouchAttribution | null,
	session: SessionData | null,
	isReturningUser: boolean,
	daysSinceFirstVisit: number | null,
): AttributionSummary {
	return {
		// First touch
		firstSource: firstTouch?.utmParams.utm_source || null,
		firstMedium: firstTouch?.utmParams.utm_medium || null,
		firstCampaign: firstTouch?.utmParams.utm_campaign || null,
		firstReferrer: firstTouch?.referrer || null,
		firstLandingPage: firstTouch?.landingPage || null,
		firstVisitDate: firstTouch
			? new Date(firstTouch.timestamp).toISOString()
			: null,

		// Last touch
		lastSource: lastTouch?.utmParams.utm_source || null,
		lastMedium: lastTouch?.utmParams.utm_medium || null,
		lastCampaign: lastTouch?.utmParams.utm_campaign || null,

		// Session
		sessionId: session?.sessionId || null,
		pageViewsInSession: session?.pageViews.length || 0,

		// Meta
		isReturningUser,
		daysSinceFirstVisit,
	};
}

/**
 * Get visitor ID (persistent across sessions).
 */
export function getVisitorIdValue(): string | null {
	return getVisitorId();
}

/**
 * Clear all journey data (for testing or privacy).
 */
export function clearJourneyData(): void {
	if (!isClient()) return;

	safeRemoveItem(localStorage, STORAGE_KEYS.FIRST_TOUCH);
	safeRemoveItem(localStorage, STORAGE_KEYS.LAST_TOUCH);
	safeRemoveItem(localStorage, STORAGE_KEYS.VISITOR_ID);
	safeRemoveItem(sessionStorage, STORAGE_KEYS.SESSION);
}

// ============================================
// Convenience Functions for Specific Flows
// ============================================

/**
 * Get UTM params in a flat format for API requests.
 */
export function getUTMParamsFlat(): Record<string, string | null> {
	const journey = getJourneyData();

	return {
		// Use last touch for campaign attribution (more relevant for conversion)
		utm_source: journey.lastTouch?.utmParams.utm_source || null,
		utm_medium: journey.lastTouch?.utmParams.utm_medium || null,
		utm_campaign: journey.lastTouch?.utmParams.utm_campaign || null,
		utm_term: journey.lastTouch?.utmParams.utm_term || null,
		utm_content: journey.lastTouch?.utmParams.utm_content || null,

		// Include first touch source for full attribution
		first_source: journey.firstTouch?.utmParams.utm_source || null,
		first_medium: journey.firstTouch?.utmParams.utm_medium || null,
		first_campaign: journey.firstTouch?.utmParams.utm_campaign || null,
	};
}

/**
 * Get journey data formatted for Meta CAPI custom_data.
 */
export function getMetaCustomData(): Record<string, string | number | null> {
	const journey = getJourneyData();
	const utm = journey.lastTouch?.utmParams;

	return {
		// UTM params (last touch for conversion attribution)
		utm_source: utm?.utm_source || null,
		utm_medium: utm?.utm_medium || null,
		utm_campaign: utm?.utm_campaign || null,

		// First touch for acquisition attribution
		first_touch_source: journey.firstTouch?.utmParams.utm_source || null,
		first_touch_campaign: journey.firstTouch?.utmParams.utm_campaign || null,

		// Journey context
		landing_page: journey.firstTouch?.landingPage || null,
		referrer: journey.firstTouch?.referrer || null,
		is_returning_user: journey.isReturningUser ? 1 : 0,
		days_since_first_visit: journey.daysSinceFirstVisit,
		session_page_views: journey.totalPageViews,
	};
}

/**
 * Track a conversion event with full journey context.
 * Useful for Umami custom events.
 */
export function trackConversionWithJourney(
	eventName: string,
	eventData?: Record<string, unknown>,
): void {
	if (!isClient() || !window.umami) return;

	const journey = getJourneyData();

	window.umami.track(eventName, {
		...eventData,
		// Attribution
		source: journey.lastTouch?.utmParams.utm_source || "direct",
		medium: journey.lastTouch?.utmParams.utm_medium || "none",
		campaign: journey.lastTouch?.utmParams.utm_campaign || null,
		// Journey
		is_returning: journey.isReturningUser,
		session_views: journey.totalPageViews,
	});
}

// TypeScript declaration for Umami
declare global {
	interface Window {
		umami?: {
			track: (eventName: string, data?: Record<string, unknown>) => void;
		};
	}
}
